import { useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
// @mui
import { alpha, styled } from '@mui/material/styles'
import { Box, Stack, Typography, IconButton } from '@mui/material'
// auth
import { useAuthContext } from '../../../auth/useAuthContext'
// components
import Iconify from '../../../components/iconify'
import Can from '../../../components/can'
import Render from '../../../components/conditional/Render'
import { useLocales } from '../../../locales'
import { useSnackbar } from '../../../components/snackbar'
import { maskDocument } from '../../../utils/textMask'
import { PATH_AFTER_LOGIN } from '../../../routes/paths'
import ImpersonateFormModal from '../header/ImpersonateFormModal'

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 1.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}))

// ----------------------------------------------------------------------

export default function NavImpersonate() {
  const navigate = useNavigate()
  const { user, impersonate } = useAuthContext()
  const { translate } = useLocales()
  const { enqueueSnackbar } = useSnackbar()
  const impersonateModal = useRef()

  const handleImpersonate = useCallback(() => {
    impersonateModal.current?.open()
  }, [])

  const handleCloseImpersonate = useCallback(async () => {
    try {
      await impersonate(null)
      navigate(PATH_AFTER_LOGIN)
      document.location.reload(true)
    } catch (error) {
      console.error(error)
      enqueueSnackbar(translate('impersonate.tryAgainLater'), { variant: 'error' })
    }
  }, [enqueueSnackbar, navigate, impersonate, translate])

  const onClose = useCallback(() => {}, [])

  if (!user) return null

  return (
    <Box sx={{ px: 2.5, mb: 2 }}>
      <ImpersonateFormModal ref={impersonateModal} onClose={onClose} />

      <Can perform="users:impersonate">
        <Render if={user.partners?.length === 0}>
          <StyledRoot
            onClick={handleImpersonate}
            sx={{
              cursor: 'pointer',
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.grey[500], 0.2) },
            }}
          >
            <Iconify icon="twemoji:bird" width={24} sx={{ transform: 'rotateY(180deg)', mr: 1 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
              Impersonar Parceiro
            </Typography>
          </StyledRoot>
        </Render>

        <Render if={user.partners?.length !== 0}>
          <StyledRoot sx={{ position: 'relative' }}>
            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 'bold', lineHeight: 1 }}
              >
                PARCEIRO
              </Typography>
              <Typography variant="subtitle2" noWrap>
                {user.partners[0]?.name}
              </Typography>
              <Typography variant="caption" noWrap sx={{ color: 'text.secondary', opacity: 0.8 }}>
                {maskDocument(user.partners[0]?.document)}
              </Typography>
            </Stack>

            <IconButton onClick={handleCloseImpersonate} size="small" sx={{ ml: 1 }}>
              <Iconify icon="solar:close-circle-bold-duotone" width={20} />
            </IconButton>
          </StyledRoot>
        </Render>
      </Can>
    </Box>
  )
}
